import { XMarkIcon } from "@heroicons/react/24/outline";
import React, { useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { useNavigate } from "react-router-dom";
import { CreateTestDeckPopup } from "../../../components/create-test-deck-popup";
import ConfirmDialog from "../../../components/dialogs/confirm-dialog";
import Loading from "../../../components/loading";
import Saving from "../../../components/saving";
import BottomProgressTracker from "./components/bottom-progress-tracker";
import ChartSearchDialog from "./components/chart-search-dialog";
import { ImportStatusModal } from "./components/import-status-modal";
import { MarkOverwritePrompt } from "./components/mark-overwrite-prompt";
import SideMenu from "./components/side-menu";
import UnsavedChangesAlertModal from "./components/unsaved-changes-alert-modal";
import { useBallotMarkup } from "./hooks/use-ballot-markup";
pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.js`;

export const BallotMarkup = () => {
  const { models, functions } = useBallotMarkup();
  // @ts-ignore
  const [zindex, setZindex] = useState("z-[100]");
  const navigate = useNavigate();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [reuploading, setReuploading] = useState(false);

  const reUploadPdf = () => {
    setShowUploadModal(true);
    setReuploading(true);
    setZindex("");
  };

  const cancelFileUpload = () => {
    setShowUploadModal(false);
    navigate(0);
    setZindex("z-[110]");
    setReuploading(false);
  };

  // @ts-ignore
  return (
    <div className={`${zindex} flex flex-col min-h-screen bg-[#F2F5FA] -mt-10`}>
      <div className="flex flex-row justify-end w-full pr-4 my-3">
        <div
          className="flex items-center justify-center w-8 h-8 bg-gray-200 rounded-full cursor-pointer"
          onClick={() => {
            const type =
              models.chart?.electionType.name === "Non Partisan"
                ? `/elections/${models.chart?.electionType.name.replace(
                  " ",
                  "-"
                )}`
                : `/elections/${models.chart?.electionType.name.split(" ")[0]}`;

            navigate(
              `${type.toLowerCase().replace(" election", "")}?status=Completed`
            );
          }}
        >
          <XMarkIcon height={20} width={20} />
        </div>
      </div>
      <div className="flex flex-row flex-grow">
        <SideMenu
          functions={functions}
          models={models}
          reUploadPdf={reUploadPdf}
        />
        <div
          className="flex justify-center w-11/12 ml-2 text-xl font-black bg-white mr-14 mb-36"
          ref={models.wrapperDivRef}
        >
          {!models.loading && (
            <Document
              className="relative flex flex-col flex-grow w-full min-w-full"
              file={models.pdfUrl}
              onLoadSuccess={functions.onDocumentLoadSuccess}
              onLoadError={(e: any) => {
                alert(e);
              }}
            >
              <>
                <Page
                  width={models.width}
                  className="relative flex-grow"
                  pageNumber={1}
                >
                  {models.currentMarks &&
                    models.currentMarks
                      .filter((m) => !m.isOnSecondPage)
                      .map((mark, i) => (
                        <div
                          key={`mark-${i}`}
                          className={`absolute ${models.chart?.ballotFile.useSquareMarks
                              ? "2xl:w-14 xl:w-14"
                              : "2xl:w-7 xl:w-6 rounded-full"
                            } h-3 md:h-4 xl:h-5 2xl:h-6 w-4 md:w-5 bg-mark-color z-20`}
                          style={models.chart?.ballotFile.useSquareMarks ? {
                            top: functions.convertYFromDBSize(mark.y) - 3,
                            left: functions.convertXFromDBSize(mark.x) - 12,
                          } : {
                            top: functions.convertYFromDBSize(mark.y) - 3,
                            left: functions.convertXFromDBSize(mark.x),
                          }}
                        >
                          {" "}
                        </div>
                      ))}
                  <div
                    className="absolute inset-0 z-100"
                    onClick={(e: any) => {
                      e.stopPropagation();
                      e.preventDefault();
                      functions.updateClick(
                        e.nativeEvent.offsetX - 10,
                        e.nativeEvent.offsetY - 6,
                        1
                      );
                    }}
                  ></div>
                </Page>
                {models.totalPages > 1 && (
                  <Page
                    width={models.width}
                    className="relative flex-grow"
                    pageNumber={2}
                  >
                    {models.currentMarks &&
                      models.currentMarks
                        .filter((m) => m.isOnSecondPage)
                        .map((mark, i) => (
                          <div
                            key={`mark-${i}`}
                            className={`absolute ${models.chart?.ballotFile.useSquareMarks
                                ? "2xl:w-14 xl:w-14"
                                : "2xl:w-7 xl:w-6 rounded-full"
                              } h-3 md:h-4 xl:h-5 2xl:h-6 w-4 md:w-5 bg-mark-color z-20`}
                            style={models.chart?.ballotFile.useSquareMarks ? {
                              top: functions.convertYFromDBSize(mark.y) - 3,
                              left: functions.convertXFromDBSize(mark.x) - 12,
                            } : {
                              top: functions.convertYFromDBSize(mark.y) - 3,
                              left: functions.convertXFromDBSize(mark.x),
                            }}
                          >
                            {" "}
                          </div>
                        ))}
                    <div
                      className="absolute inset-0 z-100"
                      onClick={(e: any) => {
                        e.stopPropagation();
                        e.preventDefault();
                        functions.updateClick(
                          e.nativeEvent.offsetX - 10,
                          e.nativeEvent.offsetY - 6,
                          2
                        );
                      }}
                    ></div>
                  </Page>
                )}
              </>
            </Document>
          )}
          {models.loading && (
            <div className="fixed h-screen inset-0 z-[100]">
              <Loading />
            </div>
          )}
        </div>
      </div>
      <BottomProgressTracker
        totalMarksCount={models.totalMarksNeeded}
        currentMarksCount={models.currentMarks.length}
        chart={models.chart}
      />
      <CreateTestDeckPopup
        chartId={`${models?.chart?.id}`}
        open={showUploadModal}
        close={cancelFileUpload}
        reuploading={reuploading}
      />
      <ConfirmDialog
        title="Reset all marks?"
        description="This will remove all marks from the ballot. Do you want to continue?"
        acceptText="YES"
        rejectText="NO"
        isOpen={models.showResetDialog}
        toggle={functions.cancelClearMarks}
        onSave={(value) => {
          value ? functions.clearMarks() : functions.cancelClearMarks();
        }}
      />
      {models.saving && !models.generating && (
        <div className="fixed h-screen inset-0 z-[100]">
          <Saving />
        </div>
      )}
      <MarkOverwritePrompt
        isOpen={models.markupOverwrite}
        toggle={functions.toggleMarkupOverwrite}
        overwriteMarks={functions.overwriteExistingMarks}
      />
      <ImportStatusModal
        isError={!models.importSuccessful}
        isOpen={models.showImportStatusModal}
        toggle={functions.toggleImportStatusModal}
        message={models.importMessage}
      />
      <ChartSearchDialog
        title="Search for previous ballot"
        searchPlaceholderText="County or municipality"
        search={functions.searchCharts}
        searchResults={models.chartSearchResults}
        setQuery={functions.updateChartSearchQuery}
        setYear={functions.updateChartSearchYear}
        query={models.chartSearchQuery}
        year={models.chartSearchYear}
        selectedChart={functions.selectedChart}
        setSelectedChart={functions.updateSelectedChart}
        importMarks={functions.importMarks}
        isOpen={models.showChartSearchDialog}
        toggle={functions.cancelChartSearch}
      />
      {models.generating && (
        <div className="fixed h-screen inset-0 z-[100]">
          <div className="flex h-screen justify-center items-center w-full flex-grow backdrop-blur bg-white bg-opacity-25 z-[100]">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 border-b-4 border-red-600 rounded-full animate-spin"></div>
              <div className="p-2 mt-4 font-semibold text-primary-blue tracking-[0.07rem]">
                GENERATING PDF
              </div>
            </div>
          </div>
        </div>
      )}
      <UnsavedChangesAlertModal hasUnsavedChanges={models.dirty} />
    </div>
  );
};
